package top.wcpe.allincore.dsl.ir.model

import top.wcpe.allincore.dsl.ir.expr.IrType
import top.wcpe.allincore.dsl.ir.model.internal.ReadOnlyOrderedMap
import kotlin.jvm.JvmStatic

/**
 * 状态默认值 IR。
 *
 * entries 仅存放字面量初值，允许的运行时类型：String / Long / Double / Boolean / null。
 * 使用 LinkedHashMap 保序后不可变包装，允许 null value（与 codec 的 NULL 类型对应）。
 *
 * M5-c #I5：新增 [types] 表，携带每个 state 字段的静态类型。
 * 仅当 DSL 作者显式书写类型标注时才会写入；未写的字段不入 types 表，
 * runtime 侧等价于 [IrType.ANY]，保持对 222 个未标注 .allin 资源的零破坏兼容。
 */
class UiState(entries: Map<String, Any?>?, types: Map<String, IrType>?) {

    val entries: Map<String, Any?>
    val types: Map<String, IrType>

    init {
        val e = entries ?: throw NullPointerException("entries")
        val t = types ?: throw NullPointerException("types")
        // 注意：Map.copyOf 不允许 null value，因此手动用 LinkedHashMap + 只读包装
        this.entries = ReadOnlyOrderedMap(LinkedHashMap(e))
        this.types = ReadOnlyOrderedMap(LinkedHashMap(t))
    }

    /** 兼容旧签名：不含类型表。 */
    constructor(entries: Map<String, Any?>?) : this(entries, emptyMap())

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is UiState) return false
        return entries == other.entries && types == other.types
    }

    // 复刻 Objects.hash(entries, types)：种子 1、31 倍累乘。
    override fun hashCode(): Int {
        var result = 1
        result = 31 * result + entries.hashCode()
        result = 31 * result + types.hashCode()
        return result
    }

    override fun toString(): String {
        // types 仅在非空时附加展示，避免污染现有调试日志。
        if (types.isEmpty()) {
            return "UiState{" + entries + "}"
        }
        return "UiState{" + entries + ",types=" + types + "}"
    }

    companion object {
        private val EMPTY = UiState(emptyMap<String, Any?>(), emptyMap<String, IrType>())

        /** 返回空状态实例。 */
        @JvmStatic
        fun empty(): UiState = EMPTY
    }
}
